import React, { useState, useEffect, useCallback, useRef } from "react";
import { listConnections, disconnectConnection, getThemeEmbedStatus } from "../../services/integrationsService";
import { useTrack } from "../../hooks/useTrack";
import { useAuth } from "../../contexts/AuthContext";
import { SALES_SOURCES } from "./salesSources";
import { ConnectCard } from "./ConnectCard";
import { isThemeEmbedFresh } from "./shopifyThemeEmbed";
import { clearShopifyInstallNonce, clearShopifyPendingConfirm } from "./useShopifyPendingConfirm";

/**
 * Builds the ordered card list: one card per non-disconnected connection
 * (registry order, then newest connection first for a repeated source), plus
 * a Connect card for every registry source that has none.
 *
 * A `disconnected` connection is treated the same as no connection at all —
 * the uniqueness key is (advertiser_id, source, external_account_id), so a
 * disconnected store stays in the table for history but must not block a
 * fresh Connect card for that source.
 */
function buildCards(connections) {
  const bySource = new Map(SALES_SOURCES.map((source) => [source.id, []]));
  for (const connection of connections) {
    if (connection.status === "disconnected") continue;
    const list = bySource.get(connection.source);
    if (list) list.push(connection);
  }

  const cards = [];
  for (const source of SALES_SOURCES) {
    const sourceConnections = (bySource.get(source.id) ?? [])
      .slice()
      .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime());

    if (sourceConnections.length === 0) {
      cards.push({ key: source.id, source, connection: null });
      continue;
    }
    for (const connection of sourceConnections) {
      cards.push({ key: connection.id, source, connection });
    }
  }
  return cards;
}

// Plain-English copy for a source's OAuth callback reason codes
// (?integration=<source>&status=error&reason=...). Kept here rather than in
// a per-source service file: this is UI copy for one specific card, not a
// service concern, and the reason vocabulary is the callback's, not a
// service return value this component already threads through.
//
// The keys here must cover `CallbackErrorReason` in both
// `supabase/functions/shopify-oauth/index.ts` and
// `supabase/functions/amazon-oauth/index.ts`. The vocabulary is shared across
// every source's callback, so treat these keys as the common contract: a
// reason a callback redirects with that has no entry here falls through to
// the generic "Something went wrong" message below.
//
// Returns the copy for one reason code, with the source's name substituted for
// the brand-specific messages so the same map serves every source. The
// source's `accountNoun` is what one connected account is called ("store"
// unless the registry says otherwise, "seller account" for Amazon).
function integrationErrorCopy(source, reason) {
  const sourceName = source.name;
  const accountNoun = source.accountNoun ?? "store";
  const copy = {
    denied: "The connection was cancelled.",
    invalid_state: "The connection request expired. Please try again.",
    invalid_hmac: `${sourceName}'s response could not be verified. Please try again.`,
    // "store" only reads naturally for a merchant-platform source like
    // Shopify, but `invalid_shop` is a reason code only Shopify's callback
    // emits (it names the store-domain mismatch check), so it stays fixed
    // copy rather than a dynamic noun.
    invalid_shop: "The store did not match the one you started connecting. Please try again.",
    missing_scope: `${sourceName} did not grant permission to read orders. Please try again and allow that permission.`,
    exchange_failed: `${sourceName} could not complete the connection. Please try again.`,
    storage_failed: "The connection could not be saved. Please try again.",
    // DEV-2371: confirm's 409 reason when another advertiser holds the store.
    store_connected_to_other_advertiser: `This ${accountNoun} is already connected to another Audiohook account. Contact support to move it.`,
    // DEV-2216, amazon-oauth's callback: the seller account Amazon returned is
    // not the one confirmed at the Login URI step.
    invalid_seller: `The ${sourceName} seller account did not match the one you started connecting. Please try again.`,
    // DEV-2216, amazon-oauth: required environment variables are missing.
    not_configured: `${sourceName} connections are not available yet. Please contact support.`,
    // Emitted client-side (AuthGate's Amazon Login URI capture, DEV-2216)
    // when persisting the captured params fails.
    amazon_storage_unavailable: "Your browser blocked storage this app needs. Allow site data and connect Amazon again.",
    // amazon-oauth's `authorize` answered 400: the captured Seller Central
    // link cannot be used (not https on a known Seller Central host, a
    // malformed selling_partner_id, a missing amazon_state).
    invalid_callback_uri: `We couldn't verify that ${sourceName} link. Connect your ${accountNoun} from here instead.`,
    // Emitted client-side when amazon-oauth's `authorize` fails any other way
    // (401, 403, 5xx, network): the captured request is kept for a retry.
    authorize_unreachable: `We couldn't reach ${sourceName} right now. Please try again.`,
    // Emitted client-side (AuthGate's install capture effect, DEV-2332) when
    // an install started from Shopify itself (?shop=&hmac=) can't proceed —
    // the begin-install call to shopify-oauth failed or the shop/hmac didn't
    // verify — never a callback reason code.
    install_link_invalid: "We couldn't verify that install link. Connect your store from here instead.",
    // Emitted client-side (AuthGate's install capture effect, second review
    // round) when persisting the browser-binding install nonce fails
    // (localStorage blocked, or its read-back didn't match) — begin-install
    // is never called in that case, since the eventual confirm could never
    // succeed without it.
    install_storage_unavailable: "Your browser blocked storage this app needs. Allow site data and try the install again.",
    // Emitted client-side (AuthGate's ?integration= effect) when an
    // origin=install redirect arrives with no matching install-nonce entry
    // in localStorage (missing, expired, or a shared-machine leftover) —
    // shown instead of silently dropping the redirect.
    install_confirm_missing: "We couldn't finish connecting your store in this browser. Install the app again from Shopify.",
    unexpected: "Something unexpected happened. Please try again.",
  };
  // `reason` comes from the URL (?reason=...), so it must never be looked up
  // as a bare property access: a value like "constructor", "toString",
  // "valueOf", or "hasOwnProperty" would then resolve to the matching
  // Object.prototype member instead of undefined, so `?? "..."` would not
  // fire and React would be handed a function as a child. Object.hasOwn
  // guards against exactly that.
  return Object.hasOwn(copy, reason) ? copy[reason] : "Something went wrong. Please try again.";
}

// Statuses that render ConnectCard's Connect / Not now prompt instead of a
// note: pending_confirm_prompt (DEV-2332, Shopify install-origin) and
// authorize_prompt (DEV-2216, Amazon Login URI).
const PROMPT_STATUSES = new Set(["pending_confirm_prompt", "authorize_prompt"]);

function isPromptStatus(status) {
  return PROMPT_STATUSES.has(status);
}

// Shown once confirmConnect settles for a source.id === integrationResult.source
// whose status was "pending" — see the pending-confirm effect below. Kept
// separate from integrationResult itself: integrationResult is one-shot
// state owned by AuthGate (it does not know whether confirm succeeded), while
// this is this component's own record of what confirming actually did.
function confirmOutcomeNoteFor(source, confirmOutcome) {
  if (!confirmOutcome || confirmOutcome.source !== source.id) return null;
  if (confirmOutcome.tone === "success") {
    return { tone: "success", text: `${source.name} connected.` };
  }
  return { tone: "error", text: confirmOutcome.text };
}

function resultNoteFor(source, integrationResult, confirmOutcome) {
  const confirmNote = confirmOutcomeNoteFor(source, confirmOutcome);
  if (confirmNote) return confirmNote;

  if (!integrationResult || integrationResult.source !== source.id) return null;
  // Pending shows no note by itself — wait for confirmOutcome once
  // confirmConnect settles (the pending-confirm effect below sets it).
  if (integrationResult.status === "pending") return null;
  // A prompt status renders its own Connect / Not now prompt via
  // ConnectCard's confirmPrompt prop, never a plain note.
  if (isPromptStatus(integrationResult.status)) return null;
  if (integrationResult.status === "connected") {
    return { tone: "success", text: `${source.name} connected.` };
  }
  const text = integrationErrorCopy(source, integrationResult.reason);
  return { tone: "error", text };
}

// Copies `obj` without `key`, e.g. clearing one card's stored error.
function withoutKey(obj, key) {
  const next = { ...obj };
  delete next[key];
  return next;
}

// Immutable Set add/remove, for the pending-action sets below: two concurrent
// disconnects (or connects) each track their own id, so one finishing never
// clears another still-in-flight card's busy state.
function withId(set, id) {
  const next = new Set(set);
  next.add(id);
  return next;
}

function withoutId(set, id) {
  const next = new Set(set);
  next.delete(id);
  return next;
}

/**
 * Container for the "Sales data" cards on the UserMenu Integrations screen.
 * Fetches the advertiser's connections when `active` becomes true, renders
 * one ConnectCard per SALES_SOURCES entry, and handles disconnect + refetch.
 */
export function SalesIntegrationsSection({ advertiserId, authToken, active, integrationResult, clearIntegrationResult }) {
  const track = useTrack();
  const { effectiveAdvertiserName, isSupportUser } = useAuth();
  // Busy state for the install-origin explicit Connect / Not now prompt
  // (DEV-2332) — separate from pendingConnects (the ordinary Connect-card
  // flow's busy set), since this prompt has no card key of its own to key
  // a Set entry off.
  const [pendingConfirmBusy, setPendingConfirmBusy] = useState(false);
  // advertiserId last successfully fetched for, mirroring
  // DataExportIntegrationCard's fetchedForRef so a support masquerade switch
  // (advertiserId changes without unmounting this component) re-fetches.
  const fetchedForRef = useRef(null);
  // Two counters, deliberately separate. `advertiserEpochRef` changes only
  // when the viewed advertiser changes, so a disconnect or connect that
  // started for advertiser A drops its result after a masquerade switch to B.
  // `fetchSeqRef` changes on every list request too, so only the newest
  // list reply is applied. Sharing one counter would make an action's own
  // post-disconnect refetch look like an advertiser switch and leave that
  // card stuck in its busy state.
  const advertiserEpochRef = useRef(0);
  const fetchSeqRef = useRef(0);
  // `${epoch}:${connection id}` of status requests awaiting a reply (DEV-2405).
  const themeStatusInFlightRef = useRef(new Set());
  // Requests currently awaiting a reply, so the visibility refresh never
  // stacks on top of one.
  const inFlightFetchesRef = useRef(0);
  // True while something the merchant started, or a pending confirm, is
  // mid-flight; the visibility refresh must not run over it. Updated on every
  // render so the listener below never goes stale.
  const refreshBlockedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connections, setConnections] = useState([]);
  // Sets, not a single id/key: two cards can be mid-disconnect (or
  // mid-connect) at once, and one finishing must only clear its own entry,
  // never the other card's still-pending busy state.
  const [pendingDisconnects, setPendingDisconnects] = useState(() => new Set());
  const [pendingConnects, setPendingConnects] = useState(() => new Set());
  // Keyed by card key (connection id, or source id for a card with no
  // connection), so a failed disconnect or connect start is reported on that
  // card and the rest of the list stays on screen.
  const [actionErrors, setActionErrors] = useState({});
  // Outcome of calling a source's confirmConnect for a "pending" result
  // (code-review MEDIUM #2). null until confirmConnect settles.
  const [confirmOutcome, setConfirmOutcome] = useState(null);
  // Guards the confirm effect below so the same token is never confirmed
  // twice (e.g. a re-render, or a parent re-passing the same integrationResult
  // object) — confirm_pending_connection is single-use, so a second call
  // would only ever fail even if it were harmless to retry.
  const confirmedTokenRef = useRef(null);
  // Guards the advertiserId effect below so it only clears integrationResult
  // on an actual switch, never on the initial mount (there is nothing stale
  // to clear yet, and a fresh page load may itself be the ?integration=
  // redirect this result came from).
  const isFirstAdvertiserRenderRef = useRef(true);

  useEffect(() => {
    advertiserEpochRef.current += 1;
    fetchSeqRef.current += 1;
    fetchedForRef.current = null;
    setConnections([]);
    setError(null);
    setLoading(false);
    setPendingDisconnects(new Set());
    setPendingConnects(new Set());
    setActionErrors({});
    setConfirmOutcome(null);
    confirmedTokenRef.current = null;
    if (!isFirstAdvertiserRenderRef.current) {
      // A support masquerade switch (or account switcher change) makes any
      // held integrationResult belong to the advertiser being left behind.
      clearIntegrationResult?.();
    }
    isFirstAdvertiserRenderRef.current = false;
  }, [advertiserId, clearIntegrationResult]);

  // DEV-2405: for each connected Shopify store, ask the integrations function
  // whether the theme embed is on and merge the answer into that connection.
  // Fire and forget: the list is already on screen and never waits for this.
  // At most one request per connection is in flight, and a reply does not depend
  // on the list's sequence number (a later list refetch would otherwise discard
  // a live answer): it is applied when it belongs to the current advertiser and
  // its `theme_embed_checked_at` is newer than what the card holds (or the card
  // holds none). A later list carries the database value, which already includes
  // the answer, so nothing is lost. Any failure is silent. No request when it
  // cannot change anything: a support user or a fresh stored "on".
  const refreshThemeEmbedStatus = useCallback((list) => {
    if (isSupportUser) return;
    const epoch = advertiserEpochRef.current;
    const nowMs = Date.now();
    for (const connection of list) {
      if (connection.source !== "shopify" || connection.status !== "connected") continue;
      if (isThemeEmbedFresh(connection, nowMs)) continue;
      const flightKey = `${epoch}:${connection.id}`;
      if (themeStatusInFlightRef.current.has(flightKey)) continue;
      themeStatusInFlightRef.current.add(flightKey);
      (async () => {
        try {
          const reply = await getThemeEmbedStatus(advertiserId, connection.id, authToken);
          if (advertiserEpochRef.current !== epoch) return;
          if (!reply || typeof reply !== "object") return;
          const { theme_embed_enabled: enabled, theme_embed_checked_at: checkedAt } = reply;
          if (typeof enabled !== "boolean" && enabled !== null) return;
          const replyMs = Date.parse(checkedAt);
          setConnections((prev) =>
            prev.map((c) => {
              if (c.id !== connection.id) return c;
              const heldMs = Date.parse(c.theme_embed_checked_at);
              const newer = Number.isNaN(heldMs) || (!Number.isNaN(replyMs) && replyMs > heldMs);
              return newer
                ? { ...c, theme_embed_enabled: enabled, theme_embed_checked_at: typeof checkedAt === "string" ? checkedAt : null }
                : c;
            }),
          );
        } catch {
          // Silent by design: the list's own values are already showing.
        } finally {
          themeStatusInFlightRef.current.delete(flightKey);
        }
      })();
    }
  }, [advertiserId, authToken, isSupportUser]);

  // Returns the freshly fetched list (or null if it couldn't be applied —
  // request superseded, or the fetch itself failed) so callers that need to
  // reason about the result synchronously (the pending-confirm effect below)
  // don't have to read back from `connections` state, which may not have
  // re-rendered yet.
  //
  // `background: true` (the tab-visibility refresh below) keeps the current
  // cards on screen: no loading skeleton, no error banner on failure, and the
  // action errors the merchant may still be reading are left alone.
  const fetchConnections = useCallback(async ({ background = false } = {}) => {
    if (!advertiserId || !authToken) return null;
    const seq = ++fetchSeqRef.current;
    inFlightFetchesRef.current += 1;
    if (!background) {
      setLoading(true);
      setError(null);
    }
    try {
      const result = await listConnections(advertiserId, authToken);
      if (fetchSeqRef.current !== seq) return null;
      setConnections(result);
      // A successful fetch means the list on screen is current, so any error
      // reported against a card that no longer applies (e.g. a stale
      // disconnect failure after the connection is gone) is stale too.
      setError(null);
      if (!background) setActionErrors({});
      refreshThemeEmbedStatus(result);
      return result;
    } catch (err) {
      if (fetchSeqRef.current !== seq) return null;
      if (!background) setError(err?.message ?? "Failed to load sales integrations");
      return null;
    } finally {
      inFlightFetchesRef.current -= 1;
      if (fetchSeqRef.current === seq && !background) setLoading(false);
    }
  }, [advertiserId, authToken, refreshThemeEmbedStatus]);

  useEffect(() => {
    if (!active || !advertiserId || !authToken) return;
    if (fetchedForRef.current === advertiserId) return;
    fetchedForRef.current = advertiserId;
    fetchConnections();
  }, [active, advertiserId, authToken, fetchConnections]);

  refreshBlockedRef.current =
    pendingDisconnects.size > 0 ||
    pendingConnects.size > 0 ||
    pendingConfirmBusy ||
    integrationResult?.status === "pending" ||
    isPromptStatus(integrationResult?.status);

  // DEV-2400: "Open theme editor" opens another tab. Coming back, refetch so
  // the card shows the worker's latest answer without closing and reopening
  // the panel. Only while the panel is active, never over an action or a
  // pending confirm, never stacked on a fetch in flight.
  useEffect(() => {
    if (!active || !advertiserId || !authToken) return undefined;
    function onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      if (refreshBlockedRef.current || inFlightFetchesRef.current > 0) return;
      fetchConnections({ background: true });
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [active, advertiserId, authToken, fetchConnections]);

  // Pending confirm flow (code-review MEDIUM #2): the OAuth callback staged
  // the connection but did not write it. Present the token back to the
  // matching source's confirmConnect exactly once, then either refetch (on
  // success) or record a plain error, never revealing which specific check
  // (missing/expired/wrong user/wrong advertiser) rejected it.
  //
  // T7 real-install bug (2026-09-21): confirmedTokenRef alone only guards
  // this within one mount of this component. The UserMenu Integrations panel
  // unmounts and remounts this component every time it closes and reopens,
  // which recreates the ref — but integrationResult (owned by AuthGate) is
  // NOT cleared just by confirming, so reopening the panel after a
  // successful reconnect re-fires this effect with the SAME, already-
  // consumed confirmToken. confirm_pending_connection is single-use, so that
  // second attempt 404s ("This connection link is invalid or has expired")
  // even though the reconnect itself already succeeded. Fix: clear the
  // one-shot result at the level that owns it — call clearIntegrationResult
  // right after confirmConnect settles (success OR failure) — so a remount
  // has nothing stale left to re-confirm. confirmedTokenRef stays as the
  // cheap same-mount guard (e.g. against a redundant re-render).
  //
  // A rejected confirmConnect is also not necessarily a real failure: the
  // SAME stale-token symptom can reach this branch (a prior confirm already
  // succeeded) even after this fix, e.g. a race between two tabs. Rather
  // than trust the rejection blindly, refetch and check whether the source
  // is actually connected — a contradictory "expired link" message on a
  // Connected card is worse than showing nothing.
  // Shared confirm sequence for both the automatic pending-confirm effect
  // below (the in-app bound flow, DEV-2211) and handlePendingConfirmConnect
  // (the install-origin explicit Connect prompt, DEV-2332): call the
  // source's confirmConnect, then unconditionally consume the one-shot
  // integrationResult and refetch, treating a freshly fetched "connected"
  // row as success even when the RPC itself rejected (the T7 stale-token
  // race documented on the effect below).
  //
  // `epoch` is the value of advertiserEpochRef captured by the caller BEFORE
  // the async work starts. If the viewed advertiser has changed by the time
  // confirmConnect settles (a masquerade switch mid-request), every further
  // step — consuming integrationResult, refetching, setting confirmOutcome —
  // is skipped: the result belongs to the advertiser left behind, the same
  // reasoning fetchConnections' own stale-response guard uses elsewhere in
  // this component. Applying this guard here, not just in the effect, is
  // what keeps the install-origin click path from showing a stale-advertiser
  // "connected"/error note too.
  // `options.shop`, when known, narrows the T7 stale-token refetch fallback
  // (isConnected below) to the SPECIFIC shop being confirmed rather than any
  // connection of that source (second review round, item 2) — otherwise a
  // DIFFERENT already-connected store for the same source could be mistaken
  // for the just-confirmed one succeeding. Only the install-origin prompt
  // flow (below) knows the shop up front (from the persisted install-nonce
  // entry); the ordinary in-app bound flow's integrationResult carries no
  // shop, so it keeps the old, non-shop-specific check.
  //
  // `options.promptMode` (second review round, item 5) is true only for the
  // install-origin explicit Connect prompt (handlePendingConfirmConnect and
  // the already-connected-same-shop auto-confirm effect below), never for
  // the automatic in-app bound-flow effect. It changes two things:
  //   - the one-shot integrationResult is consumed immediately EXCEPT on a
  //     transient failure (network error, 5xx, or any rejection without a
  //     definitive 404) — a transient failure must leave the prompt and its
  //     confirm token/nonce alone so the merchant can retry, rather than
  //     discarding a credential that might still confirm successfully.
  //   - the error copy distinguishes a definitive rejection (the same
  //     "expired or belongs to another user" text as the bound flow) from a
  //     transient one ("We couldn't connect your store right now...").
  // The bound flow (promptMode false, the default) is unchanged from before
  // this round: it always consumes the result and always uses the
  // definitive-rejection copy, since there is no prompt/retry affordance to
  // preserve there.
  //
  // Returns one of "stale" (advertiser switched mid-request — nothing else
  // was touched), "success", "definitive_rejection", or "transient", so
  // callers that need to react to the outcome (handlePendingConfirmConnect's
  // and the auto-confirm effect's decision to clear the persisted
  // localStorage entries) don't have to re-derive it.
  const runConfirmSequence = useCallback(async (source, confirmToken, epoch, options = {}) => {
    const { installNonce = null, shop = null, promptMode = false } = options;
    let confirmSucceeded = false;
    let confirmError = null;
    try {
      await source.confirmConnect(advertiserId, confirmToken, authToken, installNonce);
      confirmSucceeded = true;
    } catch (err) {
      confirmError = err;
    }
    if (advertiserEpochRef.current !== epoch) return "stale";

    // The bound (in-app) flow consumes the one-shot result immediately,
    // regardless of outcome, so a later remount of this component finds
    // nothing left to re-confirm (the T7 fix). The install-origin prompt
    // flow defers this until the outcome is known (below), so a transient
    // failure can leave it intact for a retry.
    if (!promptMode) clearIntegrationResult?.();

    const freshConnections = await fetchConnections();
    if (advertiserEpochRef.current !== epoch) return "stale";
    const isConnected = Array.isArray(freshConnections) &&
      freshConnections.some((c) =>
        c.source === source.id && c.status !== "disconnected" && (shop == null || c.external_account_id === shop));
    // DEV-2371: the store is held by another Audiohook account. Keyed on the
    // reason code the Edge Function returns (the error message), not the 409
    // status, so another conflict can't borrow this copy. Decided before the
    // isConnected fallback: that fallback also matches revoked rows (and, in
    // the in-app flow, any store), so it must never turn this rejection into
    // a success.
    const storeTaken = confirmError?.message === "store_connected_to_other_advertiser";

    if (confirmSucceeded || (isConnected && !storeTaken)) {
      if (promptMode) clearIntegrationResult?.();
      if (confirmSucceeded) track("integration_confirmed", { source: source.id });
      setConfirmOutcome({ source: source.id, tone: "success" });
      return "success";
    }

    // A definitive rejection: the bound flow has no other signal to check
    // (it always used this copy), and the prompt flow treats exactly a 404
    // ("invalid or has expired") as definitive — see shopifyService.js's
    // invoke(), which attaches the Edge Function's actual response status.
    // A store-taken rejection is definitive too: retrying cannot help.
    const definitive = !promptMode || confirmError?.status === 404 || storeTaken;
    if (definitive) {
      if (promptMode) clearIntegrationResult?.();
      setConfirmOutcome({
        source: source.id,
        tone: "error",
        text: storeTaken
          ? integrationErrorCopy(source, "store_connected_to_other_advertiser")
          : "This connection link expired or belongs to another user. Please connect again.",
      });
      return "definitive_rejection";
    }

    setConfirmOutcome({
      source: source.id,
      tone: "error",
      text: "We couldn't connect your store right now. Please try again.",
    });
    return "transient";
  }, [advertiserId, authToken, clearIntegrationResult, fetchConnections, track]);

  useEffect(() => {
    if (!integrationResult || integrationResult.status !== "pending" || !integrationResult.confirmToken) return;
    if (!advertiserId || !authToken) return;
    if (confirmedTokenRef.current === integrationResult.confirmToken) return;

    const source = SALES_SOURCES.find((s) => s.id === integrationResult.source);
    if (!source || typeof source.confirmConnect !== "function") return;

    confirmedTokenRef.current = integrationResult.confirmToken;
    const epoch = advertiserEpochRef.current;
    runConfirmSequence(source, integrationResult.confirmToken, epoch);
  }, [integrationResult, advertiserId, authToken, runConfirmSequence]);

  // Dismiss control (ConnectCard's ×) and a fresh connect/disconnect both
  // need to clear whichever note is showing, whether it came straight from
  // integrationResult or from this component's own confirmOutcome.
  function clearResultNoteFor(sourceId) {
    if (integrationResult?.source === sourceId) {
      clearIntegrationResult?.();
    }
    setConfirmOutcome((prev) => (prev?.source === sourceId ? null : prev));
  }

  async function handleDisconnect(connection, source) {
    // A fresh action on the source the note is about makes that note stale.
    clearResultNoteFor(source.id);
    const epoch = advertiserEpochRef.current;
    setPendingDisconnects((prev) => withId(prev, connection.id));
    setActionErrors((prev) => withoutKey(prev, connection.id));
    try {
      await disconnectConnection(advertiserId, connection.id, authToken);
      if (advertiserEpochRef.current !== epoch) return;
      track("integration_disconnected", { source: connection.source });
      await fetchConnections();
    } catch (err) {
      if (advertiserEpochRef.current !== epoch) return;
      const message = err?.message ?? "Please try again.";
      setActionErrors((prev) => ({ ...prev, [connection.id]: `Couldn't disconnect ${source.name}. ${message}` }));
    } finally {
      if (advertiserEpochRef.current === epoch) setPendingDisconnects((prev) => withoutId(prev, connection.id));
    }
  }

  async function handleConnectClick(source, key, value) {
    track("integration_connect_clicked", { source: source.id });
    // A fresh action on the source the note is about makes that note stale.
    clearResultNoteFor(source.id);
    if (typeof source.startConnect !== "function") return;
    const epoch = advertiserEpochRef.current;
    setPendingConnects((prev) => withId(prev, key));
    setActionErrors((prev) => withoutKey(prev, key));
    try {
      await source.startConnect(advertiserId, authToken, value);
    } catch (err) {
      if (advertiserEpochRef.current !== epoch) return;
      const message = err?.message ?? "Please try again.";
      setActionErrors((prev) => ({ ...prev, [key]: `Couldn't start connecting ${source.name}. ${message}` }));
    } finally {
      if (advertiserEpochRef.current === epoch) setPendingConnects((prev) => withoutId(prev, key));
    }
  }

  // DEV-2332: the explicit Connect / Not now prompt for an install-origin
  // pending confirm. Deliberately separate from the pending-confirm effect
  // above (which auto-confirms the IN-APP bound flow) — this one only acts
  // on a direct click, never automatically, since an attacker could send
  // their own store's confirm link to an Audiohook user.
  //
  // try/finally (DEV-2332 code-review round) so pendingConfirmBusy is never
  // stuck true if confirmConnect (or fetchConnections inside
  // runConfirmSequence) throws unexpectedly. If the viewed advertiser
  // changed mid-request (a masquerade switch), the just-consumed
  // confirmToken belongs to the advertiser left behind: clear
  // integrationResult too, rather than leaving a stale prompt/note on
  // screen for the new advertiser.
  async function handlePendingConfirmConnect(source) {
    if (!integrationResult || typeof source.confirmConnect !== "function") return;
    setPendingConfirmBusy(true);
    const epoch = advertiserEpochRef.current;
    try {
      const outcome = await runConfirmSequence(source, integrationResult.confirmToken, epoch, {
        installNonce: integrationResult.installNonce ?? null,
        shop: integrationResult.shop ?? null,
        promptMode: true,
      });
      // Second review round (item 5): only a success or a DEFINITIVE
      // rejection (the token really is dead) clears the persisted confirm
      // token/nonce. A transient failure (network error, 5xx) leaves them
      // in place — the prompt, and its Connect button, stay usable for a
      // retry rather than discarding a credential that might still confirm.
      if (outcome === "success" || outcome === "definitive_rejection") {
        clearShopifyPendingConfirm();
        clearShopifyInstallNonce();
      }
      if (outcome !== "stale" && advertiserEpochRef.current !== epoch) {
        clearIntegrationResult?.();
      }
    } finally {
      setPendingConfirmBusy(false);
    }
  }

  function handlePendingConfirmNotNow() {
    clearShopifyPendingConfirm();
    clearShopifyInstallNonce();
    clearIntegrationResult?.();
  }

  // DEV-2216: the explicit Connect / Not now prompt for an Amazon Login URI
  // request AuthGate captured. Connect calls the source's authorizeConnect,
  // which asks amazon-oauth for the Seller Central callback URL and navigates
  // there. Only a 400 means the captured link itself is unusable (e.g. not
  // Seller Central's), so only a 400 drops the request. A 401 (expired
  // session), 403, 5xx or network error keeps the request and the prompt for a
  // retry, inside Amazon's 10 minute window.
  async function handleAuthorizeConnect(source) {
    const login = integrationResult?.amazonLogin;
    if (!login || typeof source.authorizeConnect !== "function") return;
    track("integration_authorize_clicked", { source: source.id });
    setConfirmOutcome((prev) => (prev?.source === source.id ? null : prev));
    setPendingConfirmBusy(true);
    const epoch = advertiserEpochRef.current;
    try {
      await source.authorizeConnect(advertiserId, authToken, login);
      source.clearPendingAuthorize?.();
    } catch (err) {
      if (advertiserEpochRef.current !== epoch) return;
      if (err?.status === 400) {
        source.clearPendingAuthorize?.();
        clearIntegrationResult?.();
        // The server's own message is a code, not copy.
        setConfirmOutcome({
          source: source.id,
          tone: "error",
          text: integrationErrorCopy(source, "invalid_callback_uri"),
        });
      } else {
        setConfirmOutcome({ source: source.id, tone: "error", text: integrationErrorCopy(source, "authorize_unreachable") });
      }
    } finally {
      setPendingConfirmBusy(false);
    }
  }

  function handleAuthorizeNotNow(source) {
    source.clearPendingAuthorize?.();
    clearIntegrationResult?.();
  }

  // DEV-2332 code-review round: the prompt is eligible independently of
  // whether a "no connection yet" card exists for the source (item 9) — an
  // advertiser that already has a DIFFERENT Shopify store connected must
  // still be offered the new one. Never eligible for a support user
  // (masquerade is read-only by convention); the server independently
  // enforces this too (requireNonSupportCaller in shopify-oauth's confirm
  // action).
  const isAuthorizePrompt = integrationResult?.status === "authorize_prompt";
  const promptSource = isPromptStatus(integrationResult?.status)
    ? SALES_SOURCES.find((s) => s.id === integrationResult.source)
    : null;
  // DEV-2216: an authorize prompt needs the source's authorizeConnect. Unlike
  // the Shopify confirm prompt it is shown even when the same seller account
  // is already connected: going through it again is how a seller
  // reauthorizes.
  const promptEligible = Boolean(promptSource) && !isSupportUser &&
    (!isAuthorizePrompt || typeof promptSource.authorizeConnect === "function");

  // If the SAME shop is already connected for this advertiser (in ANY
  // non-disconnected status — including revoked/expired/error, e.g. a
  // 'connected' row whose token died after a quick uninstall), there is
  // nothing to ask the merchant: the pairing was already consented to, and
  // the nonce already proves this is the same browser that started the
  // install. Second review round (item 1): rather than just discarding the
  // fresh install credential (the old behavior — a pure auto-clear with no
  // confirm), auto-confirm it — this is what actually swaps in the fresh
  // token / revives a revoked or expired row via _connect_upsert. Only a
  // shop NOT linked to this advertiser at all shows the explicit prompt.
  const alreadyConnectedSameShop = promptEligible && !isAuthorizePrompt && connections.some(
    (c) => c.source === promptSource.id && c.status !== "disconnected" && c.external_account_id === integrationResult.shop,
  );

  const showPrompt = promptEligible && !alreadyConnectedSameShop;
  // Attach the prompt to exactly one card for the source — the first one in
  // display order — regardless of whether that card has a connection or is
  // the bare "no connection yet" Connect card.
  const cards = buildCards(connections);
  const promptCardKey = showPrompt ? (cards.find((c) => c.source.id === promptSource.id)?.key ?? null) : null;
  // What the prompt names and what its buttons do. An authorize prompt names
  // the seller account ("Amazon seller account A3EXAMPLE"; the
  // selling_partner_id is not secret); a confirm prompt names the shop.
  const prompt = !showPrompt
    ? null
    : isAuthorizePrompt
      ? {
          subject: `${promptSource.name} ${promptSource.accountNoun} ${integrationResult.amazonLogin?.sellingPartnerId ?? ""}`.trim(),
          onConnect: () => handleAuthorizeConnect(promptSource),
          onNotNow: () => handleAuthorizeNotNow(promptSource),
        }
      : {
          subject: integrationResult.shop,
          onConnect: () => handlePendingConfirmConnect(promptSource),
          onNotNow: () => handlePendingConfirmNotNow(),
        };

  // Guards the auto-confirm effect below so the same token is never
  // confirmed twice (confirm_pending_connection is single-use), mirroring
  // confirmedTokenRef's role for the bound-flow effect above.
  const alreadyConnectedConfirmedTokenRef = useRef(null);
  React.useEffect(() => {
    if (!alreadyConnectedSameShop) return;
    if (!integrationResult?.confirmToken) return;
    if (alreadyConnectedConfirmedTokenRef.current === integrationResult.confirmToken) return;
    alreadyConnectedConfirmedTokenRef.current = integrationResult.confirmToken;

    const epoch = advertiserEpochRef.current;
    (async () => {
      const outcome = await runConfirmSequence(promptSource, integrationResult.confirmToken, epoch, {
        installNonce: integrationResult.installNonce ?? null,
        shop: integrationResult.shop ?? null,
        promptMode: true,
      });
      if (outcome === "success" || outcome === "definitive_rejection") {
        clearShopifyPendingConfirm();
        clearShopifyInstallNonce();
      }
    })();
  }, [alreadyConnectedSameShop, integrationResult, promptSource, runConfirmSequence]);

  return (
    <div aria-busy={loading}>
      {loading &&
        SALES_SOURCES.map((source) => <SkeletonCard key={source.id} />)}

      {!loading && error && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: "12px 16px",
            border: "1px solid #E2E2E2",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#151515" }}>Couldn't load sales integrations</div>
            <div style={{ fontSize: 11, color: "#c62828", marginTop: 2, lineHeight: 1.4 }}>{error}</div>
          </div>
          <button
            type="button"
            onClick={() => fetchConnections()}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              border: "1px solid #E2E2E2",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              background: "#FAFAFA",
              color: "#3E3E3E",
              whiteSpace: "nowrap",
              fontFamily: "inherit",
              flexShrink: 0,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Retry
          </button>
        </div>
      )}

      {!loading &&
        !error &&
        cards.map(({ key, source, connection }) => {
          const showConfirmPrompt = prompt !== null && key === promptCardKey;
          return (
            <ConnectCard
              key={key}
              source={source}
              connection={connection}
              onConnectClick={(value) => handleConnectClick(source, key, value)}
              onDisconnect={() => handleDisconnect(connection, source)}
              disconnecting={connection ? pendingDisconnects.has(connection.id) : false}
              connecting={pendingConnects.has(key)}
              connectBlockedNote={isSupportUser && source.memberOnlyConnect ? "You have read-only access for this advertiser." : null}
              actionError={actionErrors[key] ?? null}
              resultNote={resultNoteFor(source, integrationResult, confirmOutcome)}
              onDismissResult={() => clearResultNoteFor(source.id)}
              confirmPrompt={
                showConfirmPrompt
                  ? {
                      subject: prompt.subject,
                      advertiserName: effectiveAdvertiserName ?? "this advertiser",
                      busy: pendingConfirmBusy,
                      onConnect: prompt.onConnect,
                      onNotNow: prompt.onNotNow,
                    }
                  : null
              }
            />
          );
        })}
    </div>
  );
}

// Mirrors ConnectCard's layout (icon tile, two text lines, a 125px action) so
// the list does not jump when the connections arrive.
function SkeletonCard() {
  // maxWidth + the middle column's minWidth: 0 let the placeholders shrink in
  // the narrow Integrations panel instead of pushing the action bar out of
  // the card.
  const bar = (width, height) => ({ width, maxWidth: "100%", height, background: "#F0F0F0", borderRadius: 4 });
  return (
    <div
      aria-hidden="true"
      style={{
        marginTop: 12,
        border: "1px solid #E2E2E2",
        borderRadius: 12,
        padding: "14px 16px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <div style={{ width: 42, height: 42, borderRadius: 10, background: "#F0F0F0", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8, paddingTop: 2 }}>
        <div style={bar(90, 12)} />
        <div style={bar(140, 10)} />
      </div>
      <div style={{ width: 125, height: 30, background: "#F0F0F0", borderRadius: 8, flexShrink: 0 }} />
    </div>
  );
}
